/* eslint-disable */
/*!
   _____ ______ __  __ _    _  _____ __  __ ______ _____  
  / ____|  ____|  \/  | |  | |/ ____|  \/  |  ____|  __ \ 
 | |  __| |__  | \  / | |__| | (___ | \  / | |__  | |  | |
 | | |_ |  __| | |\/| |  __  |\___ \| |\/| |  __| | |  | |
 | |__| | |____| |  | | |  | |____) | |  | | |____| |__| |
  \_____|______|_|  |_|_|  |_|_____/|_|  |_|______|_____/                                                                                                                                                                                                                                                                                                   
=========================================================
* GEMHSMED UI - v2.0.0
=========================================================

* Copyright 2024 GEMHS (https://gemhs.net/)

* Designed and Coded by GEMHS

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 

*/

// Chakra imports
import {
    Box,
    Icon,
    IconButton,
    Spinner,
    SimpleGrid,
    useColorModeValue,
    useDisclosure,
    ModalOverlay,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Checkbox,
    Textarea,
    Button,
    Heading,
    Modal,
    ModalBody,
    ModalHeader,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    Text,
    Flex,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
} from "@chakra-ui/react";

// Custom components
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect, useMemo } from "react";
import { MdAddCircleOutline, MdPerson } from "react-icons/md";
import { PiTestTubeFill } from "react-icons/pi";
import { DeleteIcon, InfoIcon, SearchIcon } from "@chakra-ui/icons";
import {
    useGetAppointmentsQuery,
    useCreateAppointmentMutation,
    useDeleteAppointmentMutation,
} from "store";
import { useForm } from "react-hook-form";
import { differenceInYears } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import moment from "moment";

function PaginatedTable({ columns, initialPageSize = 10 }) {
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [searchQuery, setSearchQuery] = useState("");
    const [triggeredSearchQuery, setTriggeredSearchQuery] = useState(""); // Store triggered search query

    const { data: appointments, isFetching, error } = useGetAppointmentsQuery(
        { page: pageIndex + 1, pageSize },
        {
            keepPreviousData: true,
        }
    );

    // Safely handle undefined appointments and default to last page as 0
    const lastPage =
        appointments && appointments.count
            ? Math.ceil(appointments.count / pageSize) - 1
            : 0;
    const currentPage = pageIndex + 1;

    if (error) return <Text color="red.500">Error: {error.message}</Text>;
    if (isFetching) return <Spinner size="lg" />;

    const handleSearch = () => {
        setTriggeredSearchQuery(searchQuery); // Set the search query to be used in the API call
        setSearchQuery(""); // Optionally clear the input field after search
    };

    const handleReset = () => {
        handleSearch();
    };

    return (
        <Box overflowX="auto">
            <Table variant="striped" colorScheme="teal">
                <Thead>
                    <Tr>
                        {columns.map((column) => (
                            <Th key={column.id}>{column.header}</Th>
                        ))}
                    </Tr>
                </Thead>
                <Tbody>
                    {appointments.results.length > 0 ? (
                        appointments?.results.map((row, i) => (
                            <Tr key={i}>
                                {columns.map((column) => (
                                    <Td key={column.id}>
                                        {typeof column.accessor === "function"
                                            ? column.accessor(row)
                                            : row[column.accessor]}
                                    </Td>
                                ))}
                            </Tr>
                        ))
                    ) : (
                        <Tr>
                            <Td colSpan={columns.length} textAlign="center">
                                No appointments available
                            </Td>
                        </Tr>
                    )}
                </Tbody>
            </Table>
            <Flex justifyContent="space-between" alignItems="center" p="4">
                <Button
                    onClick={() => setPageIndex(0)}
                    disabled={pageIndex === 0}
                >
                    {"<<"}
                </Button>
                <Button
                    onClick={() => setPageIndex((old) => Math.max(old - 1, 0))}
                    disabled={pageIndex === 0}
                >
                    {"<"}
                </Button>
                <Text>
                    Page {currentPage} of {lastPage + 1}
                </Text>
                <span>
                    | Go to page:{" "}
                    <Input
                        type="number"
                        value={pageIndex + 1}
                        onChange={(e) => {
                            const page = e.target.value
                                ? Number(e.target.value) - 1
                                : 0;
                            setPageIndex(page);
                        }}
                        style={{ width: "100px" }}
                    />
                </span>
                <Button
                    onClick={() =>
                        setPageIndex((old) => Math.min(old + 1, lastPage))
                    }
                    disabled={pageIndex >= lastPage}
                >
                    {">"}
                </Button>
                <Button
                    onClick={() => setPageIndex(lastPage)}
                    disabled={pageIndex >= lastPage}
                >
                    {">>"}
                </Button>
                <Select
                    ml="4"
                    width="auto"
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPageIndex(0); // Reset to the first page after changing page size
                    }}
                >
                    {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>
                            Show {size}
                        </option>
                    ))}
                </Select>
            </Flex>
        </Box>
    );
}

function Appointments() {
    const { data: appointments, isFetching, error } = useGetAppointmentsQuery({
        page: 1,
        pageSize: 10,
    });

    const appointmentCount = appointments ? appointments.count : 0;
    const [
        deleteAppointment,
        { isLoading: isDeleting },
    ] = useDeleteAppointmentMutation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const navigate = useNavigate();
    const [appointmentToDelete, setAppointmentToDelete] = useState(null);

    const handleDetailsClick = (id) => {
        navigate(`/appointments/${id}/details`);
    };

    const handleDeleteClick = (id) => {
        setAppointmentToDelete(id);
        onOpen();
    };

    const confirmDelete = async () => {
        try {
            if (appointmentToDelete) {
                await deleteAppointment(appointmentToDelete);
                setAppointmentToDelete(null);
                onClose();
            }
        } catch (error) {
            console.error("Error deleting appointment:", error);
        }
    };

    const [createAppointment, results] = useCreateAppointmentMutation();
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
    } = useForm();

    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const capitalizeFirstLetter = (str) => {
        return str
            .split(" ")
            .map(
                (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(" ");
    };

    const onSubmit = async (data) => {
        try {
            await createAppointment(data);
            reset();
            handleClose();
        } catch (error) {
            console.error("Error creating appointment:", error);
        }
    };

    const SERVICE_CHOICES = {
        chk_up: "Check Up",
        fill: "Filling",
        ext: "Extraction",
        clean: "Cleaning",
        rct: "Root Canal Therapy",
        oth: "Other",
    };

    const columns = useMemo(
        () => [
            {
                id: "name",
                header: "Name",
                accessor: (row) => row.name,
            },
            { id: "phone", header: "Phone", accessor: (row) => row.phone },
            {
                id: "date",
                header: "Date of Appointment",
                accessor: (row) => moment(row.date).format("MMMM D, YYYY"),
            },
            {
                id: "time",
                header: "Time of Appointment",
                accessor: (row) =>
                    moment(row.time, "HH:mm:ss.SSSSSS").format("hh:mm A"),
            },
            {
                id: "service_choice",
                header: "Service",
                accessor: (row) => SERVICE_CHOICES[row.service_choice] || row.service_choice,
            },
            {
                id: "actions",
                header: "Actions",
                accessor: (row) => (
                    <Box>
                        <IconButton
                            aria-label="View details"
                            icon={<InfoIcon />}
                            onClick={() => handleDetailsClick(row.id)}
                            mr="2"
                        />
                        <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            onClick={() => handleDeleteClick(row.id)}
                            colorScheme="red"
                        />
                    </Box>
                ),
            },
        ],
        []
    );

    // Chakra Color Mode
    const brandColor = useColorModeValue("brand.500", "white");
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const [scrollBehavior, setScrollBehavior] = React.useState("inside");

    return (
        <>
            <Helmet>
                <title>Appointment List</title>
            </Helmet>
            <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
                <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
                    gap="20px"
                    mb="20px"
                >
                    {isFetching ? (
                        <Box display="flex" justifyContent="center">
                            <Spinner size="xl" />
                        </Box>
                    ) : error ? (
                        <p>Error</p>
                    ) : (
                        <MiniStatistics
                            startContent={
                                <IconBox
                                    w="56px"
                                    h="56px"
                                    bg={boxBg}
                                    icon={
                                        <Icon
                                            w="32px"
                                            h="32px"
                                            as={MdPerson}
                                            color={brandColor}
                                        />
                                    }
                                />
                            }
                            name="Total Appointments"
                            value={
                                isFetching ? (
                                    <Spinner size="lg" />
                                ) : error ? (
                                    <Text>Error fetching appointments</Text>
                                ) : (
                                    appointmentCount
                                )
                            }
                        />
                    )}
                </SimpleGrid>

                <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Confirm Deletion</ModalHeader>
                        <ModalBody>
                            <Text>
                                Are you sure you want to delete this
                                appointment?
                            </Text>
                        </ModalBody>
                        <ModalFooter>
                            <Button colorScheme="blue" mr={3} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={confirmDelete}
                                isLoading={isDeleting}
                            >
                                Delete
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                <Flex justify="flex-end" mb="4">
                    <Button leftIcon={<MdAddCircleOutline />} isLoading={results.isLoading} onClick={handleShow}>
                        New Appointment
                    </Button>
                </Flex>

                {/* Create modal form */}

                <Modal
                    isOpen={show}
                    onClose={handleClose}
                    isCentered
                    {...appointments}
                    closeOnOverlayClick={false}
                    scrollBehavior={scrollBehavior}
                    size="4xl"
                >
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>New Appointment</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                {/* Form fields with register and error handling */}

                                {/* Row 1 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={6}
                                    mb={6}
                                >
                                    {/* Name */}
                                    <Box>
                                        <FormControl isInvalid={errors.name}>
                                            <FormLabel>Name</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("name", {
                                                    required: true,
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "name",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                            {errors.name && (
                                                <FormErrorMessage>
                                                    Name is required
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    </Box>

                                    {/* Telephone Number */}
                                    <Box>
                                        <FormControl isInvalid={errors.phone}>
                                            <FormLabel>
                                                Telephone Number
                                            </FormLabel>
                                            <Input
                                                type="tel"
                                                {...register("phone", {
                                                    valueAsNumber: true,
                                                })}
                                            />
                                            {errors.phone && (
                                                <FormErrorMessage>
                                                    Telephone number is required
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>
        
                                {/* Row 2 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={4}
                                    mb={4}
                                >
                                    {/* Dentist Choice */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>
                                                Choice of Dentist
                                            </FormLabel>
                                            <Select
                                                {...register("dent_choice")}
                                            >
                                                <option value="ROSE">
                                                    Dr. Naale Rose
                                                </option>
                                                <option value="ANDREW">
                                                    Mr. Andrew Williams
                                                </option>
                                                <option value="NONE">
                                                    No Preference
                                                </option>
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    {/* Service Choice */}
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Service</FormLabel>
                                            <Select
                                                {...register("service_choice")}
                                            >
                                                <option value="chk_up">
                                                    Check Up
                                                </option>
                                                <option value="fill">
                                                    Filling
                                                </option>
                                                <option value="ext">
                                                    Extraction
                                                </option>
                                                <option value="clean">
                                                    Cleaning
                                                </option>
                                                <option value="rct">
                                                    Root Canal Therapy
                                                </option>
                                                <option value="other">
                                                    Other
                                                </option>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Name */}
                                <Box>
                                        <FormControl isInvalid={errors.name}>
                                            <FormLabel>Address</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("address", {
                                                    required: true,
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "address",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                            {errors.name && (
                                                <FormErrorMessage>
                                                    Address is required
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    </Box>

                                {/* Row 3 */}
                                <SimpleGrid
                                    columns={{ base: 1, md: 3 }}
                                    spacing={6}
                                    mb={6}
                                >
                                    {/* Date of Appointment */}
                                    <Box>
                                        <FormControl isInvalid={errors.date}>
                                            <FormLabel>Date</FormLabel>
                                            <Input
                                                type="date"
                                                {...register("date", {
                                                    required: true,
                                                })}
                                            />
                                            {errors.date && (
                                                <FormErrorMessage>
                                                    Date of Appointment is
                                                    required
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    </Box>

                                    {/* Time of Appointment */}
                                    <Box>
                                        <FormControl isInvalid={errors.time}>
                                            <FormLabel>Time</FormLabel>
                                            <Input
                                                type="time"
                                                {...register("time", {
                                                    required: true,
                                                })}
                                            />
                                            {errors.time && (
                                                <FormErrorMessage>
                                                    Time of Appointment is
                                                    required
                                                </FormErrorMessage>
                                            )}
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                <SimpleGrid>
                                    <Box>
                                        <Button
                                            fontSize="sm"
                                            variant="outline"
                                            fontWeight="500"
                                            type="submit"
                                            w="20%"
                                            h="50"
                                            mb="24px"
                                            isLoading={results.isLoading}
                                            loadingText="Loading..."
                                            spinner={<Spinner />}
                                        >
                                            Save
                                        </Button>
                                    </Box>
                                </SimpleGrid>
                            </form>
                        </ModalBody>
                    </ModalContent>
                </Modal>

                {/* End form */}

                <SimpleGrid
                    columns={{ base: 1, md: 1, xl: 1 }}
                    gap="20px"
                    mb="20px"
                >
                    {isFetching ? (
                        <Box display="flex" justifyContent="center">
                            <Spinner size="xl" />
                        </Box>
                    ) : error ? (
                        <Box>Error fetching appointment data</Box>
                    ) : (
                        <PaginatedTable
                            columns={columns}
                            initialPageSize={10}
                        />
                    )}
                </SimpleGrid>
            </Box>
        </>
    );
}

export default Appointments;
